// /settings/webauthn — Manage WebAuthn passkey credentials (list + add + delete).
// Spec: docs/plans/admin-tooling.md UI table (~line 1546).
//
// This is the enrollment UI that scripts/staff-enroll-bootstrap.ts defers to:
// bootstrap creates the staff account + TOTP, and staff come here afterward
// to register one or more WebAuthn passkeys.
//
// Delete of the LAST remaining credential requires step-up re-auth (falls
// back to TOTP-only login otherwise) — reuses the existing StepUpModal /
// action-intent pattern (D-62) with actionIntent="webauthn-delete-last".

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import {
  decodeCreationOptions,
  serializeRegistrationResponse,
  type WebAuthnCreationOptionsJSON,
} from '@/lib/webauthn';
import { StepUpModal } from '@/components/admin/step-up-modal';
import { Button, Input, Field, ErrorChip, PageHeader, Panel, PanelHeader, ConfirmDialog } from '@awakenedlands/ui';

// ── Types ─────────────────────────────────────────────────────────────────────

interface WebAuthnCredentialRow {
  credential_id: string;
  display_name: string;
  created_at: number;
  last_used_at: number | null;
  transports?: string[];
}

interface WebAuthnListResponse {
  ok: boolean;
  credentials: WebAuthnCredentialRow[];
}

interface RegisterOptionsResponse {
  ok: boolean;
  challenge_id: string;
  options: WebAuthnCreationOptionsJSON;
}

interface RegisterVerifyResponse {
  ok: boolean;
  credential_id: string;
  display_name: string;
  created_at: number;
}

interface ErrorBody {
  error?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(unixSec: number): string {
  return new Date(unixSec * 1000).toLocaleString();
}

function webAuthnSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.credentials?.create === 'function'
  );
}

function registerErrorMessage(status: number, error: string | undefined): string {
  if (status === 409 && error === 'credential_cap_reached') {
    return "You've reached the maximum of 5 passkeys. Remove one before adding another.";
  }
  if (status === 409 && error === 'credential_exists') {
    return 'This authenticator is already registered.';
  }
  if (status === 401) {
    return 'The registration challenge expired. Please try again.';
  }
  if (error === 'invalid_display_name') {
    return 'Please enter a valid name (1–64 characters).';
  }
  if (error === 'registration_failed') {
    return 'Registration failed. Please try again.';
  }
  return 'Failed to register passkey. Please try again.';
}

// ── RegisterPasskeyForm ───────────────────────────────────────────────────────

interface RegisterPasskeyFormProps {
  onRegistered: () => void;
  onCancel: () => void;
}

function RegisterPasskeyForm({ onRegistered, onCancel }: RegisterPasskeyFormProps) {
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const trimmedName = displayName.trim();
  const nameValid = trimmedName.length > 0 && trimmedName.length <= 64;

  async function handleRegister() {
    if (!nameValid) return;

    if (!webAuthnSupported()) {
      setErrorMsg('This browser does not support WebAuthn passkeys.');
      return;
    }

    setBusy(true);
    setErrorMsg('');

    // 1. Get creation options
    const optionsRes = await apiFetch<RegisterOptionsResponse>(
      '/admin/auth/webauthn/register-options',
      { method: 'POST', body: JSON.stringify({}) },
    );

    if (!optionsRes.ok) {
      setErrorMsg(registerErrorMessage(optionsRes.status, (optionsRes.data as unknown as ErrorBody).error));
      setBusy(false);
      return;
    }

    const { challenge_id: challengeId, options } = optionsRes.data;

    // 2. Invoke native browser WebAuthn creation ceremony
    let credential: PublicKeyCredential;
    try {
      const result = await navigator.credentials.create({
        publicKey: decodeCreationOptions(options),
      });

      if (!result || result.type !== 'public-key') {
        throw new Error('No credential returned.');
      }

      credential = result as PublicKeyCredential;
    } catch (e) {
      setBusy(false);
      const name = e instanceof Error ? e.name : '';
      if (name === 'NotAllowedError') {
        setErrorMsg('Passkey registration was cancelled.');
      } else if (name === 'InvalidStateError') {
        setErrorMsg('This authenticator is already registered.');
      } else {
        setErrorMsg('Passkey registration failed. Please try again.');
      }
      return;
    }

    // 3. Serialize + send to register-verify
    const verifyRes = await apiFetch<RegisterVerifyResponse>(
      '/admin/auth/webauthn/register-verify',
      {
        method: 'POST',
        body: JSON.stringify({
          challenge_id: challengeId,
          display_name: trimmedName,
          credential: serializeRegistrationResponse(credential),
        }),
      },
    );

    setBusy(false);

    if (!verifyRes.ok) {
      setErrorMsg(registerErrorMessage(verifyRes.status, (verifyRes.data as unknown as ErrorBody).error));
      return;
    }

    setDisplayName('');
    onRegistered();
  }

  return (
    <Panel>
      <PanelHeader>Register a New Passkey</PanelHeader>
      <div className="p-4 space-y-4">
        <Field label="Name this passkey">
          <Input
            id="webauthn-display-name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={64}
            placeholder="e.g. YubiKey 5, MacBook Touch ID"
            disabled={busy}
            data-testid="webauthn-display-name-input"
          />
        </Field>

        {errorMsg && <ErrorChip>{errorMsg}</ErrorChip>}

        <div className="flex items-center gap-3">
          <Button
            type="button"
            onClick={() => void handleRegister()}
            disabled={busy || !nameValid}
            data-testid="webauthn-register-submit"
          >
            {busy ? 'Waiting for security key…' : 'Register Passkey'}
          </Button>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="text-sm text-ink-low hover:text-ink-mid underline"
          >
            Cancel
          </button>
        </div>
      </div>
    </Panel>
  );
}

// ── WebauthnSettingsPage ──────────────────────────────────────────────────────

export function WebauthnSettingsPage() {
  const queryClient = useQueryClient();
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<WebAuthnCredentialRow | null>(null);
  const [stepUpDelete, setStepUpDelete] = useState<WebAuthnCredentialRow | null>(null);
  const [deleteError, setDeleteError] = useState('');

  const { data, isLoading, isError } = useQuery<WebAuthnListResponse>({
    queryKey: ['webauthn-credentials'],
    queryFn: async () => {
      const res = await apiFetch<WebAuthnListResponse>('/admin/auth/webauthn');
      if (!res.ok) throw new Error('Failed to load passkeys.');
      return res.data;
    },
  });

  const credentials = data?.credentials ?? [];

  function refreshList() {
    setShowRegisterForm(false);
    void queryClient.invalidateQueries({ queryKey: ['webauthn-credentials'] });
  }

  async function performDelete(credentialId: string, stepupToken?: string): Promise<'ok' | 'stepup_required' | 'error'> {
    const headers: Record<string, string> = {};
    if (stepupToken) headers['X-Stepup-Token'] = stepupToken;

    const res = await apiFetch(`/admin/auth/webauthn/${encodeURIComponent(credentialId)}`, {
      method: 'DELETE',
      headers,
    });

    if (res.ok) return 'ok';

    const body = res.data as unknown as ErrorBody;
    // Admin step-up family literals (middleware/stepup-required.ts) — NOT the
    // player-side 'stepup_required' used by routes/auth/*.
    if (
      res.status === 403 &&
      (body.error === 'stepup_token_missing' || body.error === 'stepup_token_invalid_or_expired')
    ) {
      return 'stepup_required';
    }
    return 'error';
  }

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    setDeleteError('');

    const result = await performDelete(target.credential_id);

    if (result === 'ok') {
      refreshList();
      return;
    }

    if (result === 'stepup_required') {
      setStepUpDelete(target);
      return;
    }

    setDeleteError('Failed to delete passkey. Please try again.');
  }

  async function handleStepUpVerified(token: string) {
    const target = stepUpDelete;
    setStepUpDelete(null);
    if (!target) return;

    const result = await performDelete(target.credential_id, token);

    if (result === 'ok') {
      refreshList();
    } else {
      setDeleteError('Failed to delete passkey. Please try again.');
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Passkeys"
        description="Manage the WebAuthn security keys and platform authenticators used to sign in and to authorize sensitive actions."
        actions={
          !showRegisterForm && (
            <Button type="button" onClick={() => setShowRegisterForm(true)} data-testid="add-passkey-btn">
              Register Passkey
            </Button>
          )
        }
      />

      {showRegisterForm && (
        <RegisterPasskeyForm onRegistered={refreshList} onCancel={() => setShowRegisterForm(false)} />
      )}

      {deleteError && <ErrorChip>{deleteError}</ErrorChip>}

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-ink-low border-t-primary" />
        </div>
      )}

      {/* Error */}
      {isError && <ErrorChip>Failed to load passkeys.</ErrorChip>}

      {/* Empty state */}
      {!isLoading && !isError && credentials.length === 0 && (
        <Panel>
          <div className="p-6 text-center space-y-2" data-testid="webauthn-empty-state">
            <p className="text-sm font-medium text-ink-high">No passkeys registered yet.</p>
            <p className="text-sm text-ink-mid">
              A passkey lets you sign in with WebAuthn and authorize sensitive step-up actions
              without typing a password or authenticator code. Register your first passkey to
              get started.
            </p>
          </div>
        </Panel>
      )}

      {/* List */}
      {!isLoading && !isError && credentials.length > 0 && (
        <div className="overflow-x-auto rounded-sm border border-border">
          <table className="w-full text-sm" data-testid="webauthn-credentials-table">
            <thead className="bg-muted text-left">
              <tr>
                <th className="px-3 py-2 font-medium text-ink-mid">Name</th>
                <th className="px-3 py-2 font-medium text-ink-mid">Created</th>
                <th className="px-3 py-2 font-medium text-ink-mid">Last used</th>
                <th className="px-3 py-2 font-medium text-ink-mid" />
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {credentials.map((cred) => (
                <tr key={cred.credential_id} className="hover:bg-panel-elev" data-testid={`webauthn-row-${cred.credential_id}`}>
                  <td className="px-3 py-2 text-ink-high">{cred.display_name}</td>
                  <td className="px-3 py-2 text-ink-low whitespace-nowrap font-mono tabular-nums text-xs">
                    {formatDate(cred.created_at)}
                  </td>
                  <td className="px-3 py-2 text-ink-low whitespace-nowrap font-mono tabular-nums text-xs">
                    {cred.last_used_at !== null ? formatDate(cred.last_used_at) : 'Never'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setPendingDelete(cred)}
                      data-testid={`delete-webauthn-${cred.credential_id}`}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirm */}
      {pendingDelete && (
        <ConfirmDialog
          title={`Delete "${pendingDelete.display_name}"?`}
          body={
            credentials.length === 1
              ? 'This is your last passkey. Removing it means you will fall back to TOTP-only login and will not be able to use WebAuthn for sign-in or step-up until you register a new passkey.'
              : 'This passkey will no longer be usable to sign in or to authorize sensitive actions.'
          }
          confirmLabel="Delete"
          destructive
          onConfirm={() => void handleConfirmDelete()}
          onCancel={() => setPendingDelete(null)}
        />
      )}

      {/* Step-up for deleting the last credential */}
      {stepUpDelete && (
        <StepUpModal
          actionIntent="webauthn-delete-last"
          targetUserId={null}
          onVerified={(token) => void handleStepUpVerified(token)}
          onCancel={() => setStepUpDelete(null)}
        />
      )}
    </div>
  );
}
